<script lang="ts">
  import { onMount } from 'svelte'
  import { settingsActions } from '$lib/stores/settings'
  import { useKeyboard } from '$lib/hooks/useEventListener'
  import { useTimeout } from '$lib/hooks/useTimer'
  import { useError } from '$lib/hooks/useError'
  import { errorService } from '$lib/services/ErrorService'
  import { clearAllLocalStorage, clearVaultStorage } from '$utils/clearLocalStorage'
  
  // Error handling setup
  const { tryExecute } = useError('DevMenu')
  
  // Accept showDevMenu as bindable prop
  interface Props {
    showDevMenu?: boolean
  }
  
  let { showDevMenu = $bindable(false) }: Props = $props()
  
  let importStatus = $state('')
  let importedKeys = $state<Record<string, boolean>>({})
  
  // Create reload timer (don't auto-start)
  const reloadTimer = useTimeout(() => {
    window.location.reload()
  }, 1000, false)
  
  // Listen for menu events from Electron
  onMount(() => {
    if (window.api) {
      // Toggle dev menu panel
      window.api.onDevToggleMenu?.(() => {
        showDevMenu = !showDevMenu
      })
      
      // Import API keys event
      window.api.onDevImportKeys?.(() => {
        importApiKeysFromEnv()
      })
      
      // Clear local storage event (legacy)
      window.api.onDevClearStorage?.(() => {
        clearLocalStorage()
      })
      
      // Clear all storage event
      window.api.onDevClearAllStorage?.(() => {
        clearLocalStorage()
      })
      
      // Clear vault storage event
      window.api.onDevClearVaultStorage?.(() => {
        clearRecentVaults()
      })
      
      // Reset usage limits event
      window.api.onDevResetUsageLimits?.(() => {
        resetUsageLimit()
      })
    }
  })
  
  async function importApiKeysFromEnv() {
    importStatus = 'Importing API keys from environment...'
    importedKeys = {}
    
    const [result, error] = await tryExecute(
      async () => {
        // Request environment variables from main process
        const envKeys = await window.electronAPI?.getEnvApiKeys?.()
        
        if (!envKeys) {
          throw new Error('Failed to get environment variables')
        }
        
        const updates: any = {}
        
        // Map environment variables to settings
        if (envKeys.OPENAI_API_KEY && envKeys.OPENAI_API_KEY !== 'your_openai_api_key_here') {
          updates.openaiApiKey = envKeys.OPENAI_API_KEY
          importedKeys['OpenAI'] = true
        }
        
        if (envKeys.ANTHROPIC_API_KEY && envKeys.ANTHROPIC_API_KEY !== 'your_anthropic_api_key_here') {
          updates.anthropicApiKey = envKeys.ANTHROPIC_API_KEY
          importedKeys['Anthropic'] = true
        }
        
        if (envKeys.GOOGLE_API_KEY && envKeys.GOOGLE_API_KEY !== 'your_google_api_key_here') {
          updates.googleApiKey = envKeys.GOOGLE_API_KEY
          importedKeys['Google'] = true
        }
        
        if (envKeys.UPSTAGE_API_KEY && envKeys.UPSTAGE_API_KEY !== 'your_upstage_api_key_here') {
          updates.upstageApiKey = envKeys.UPSTAGE_API_KEY
          importedKeys['Upstage'] = true
        }
        
        // Update AI settings
        if (Object.keys(updates).length > 0) {
          settingsActions.updateAISettings(updates)
          
          // Also update providers
          const providers = settingsActions.getAIProviders()
          const updatedProviders = providers.map(p => {
            if (p.id === 'openai' && updates.openaiApiKey) {
              return { ...p, apiKey: updates.openaiApiKey }
            }
            if (p.id === 'anthropic' && updates.anthropicApiKey) {
              return { ...p, apiKey: updates.anthropicApiKey }
            }
            if (p.id === 'google' && updates.googleApiKey) {
              return { ...p, apiKey: updates.googleApiKey }
            }
            if (p.id === 'upstage' && updates.upstageApiKey) {
              return { ...p, apiKey: updates.upstageApiKey }
            }
            return p
          })
          
          settingsActions.updateAIProviders(updatedProviders)
          
          // Force save to localStorage to trigger updates
          const currentSettings = settingsActions.exportSettings()
          localStorage.setItem('korean-grammar:settings', currentSettings)
          
          // Dispatch storage event to notify other components
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'korean-grammar:settings',
            newValue: currentSettings,
            url: window.location.href
          }))
          
          const importedCount = Object.keys(importedKeys).length
          importStatus = `Successfully imported ${importedCount} API key${importedCount !== 1 ? 's' : ''}`
          errorService.showSuccess(importStatus)
        } else {
          importStatus = 'No valid API keys found in environment'
          errorService.showWarning(importStatus)
        }
        
        return updates
      },
      {
        severity: 'error',
        context: { action: 'importApiKeys' },
        showToUser: true
      }
    )
    
    if (error) {
      importStatus = 'Error importing API keys'
      errorService.handleApiError(error, { component: 'DevMenu', action: 'importApiKeys' })
    }
  }
  
  function clearLocalStorage() {
    if (confirm('This will clear all settings and data. Are you sure?')) {
      clearAllLocalStorage()
      importStatus = 'Local storage cleared. Reloading app...'
      reloadTimer.restart()
    }
  }
  
  function clearRecentVaults() {
    if (confirm('This will clear all vault and recent folders data. Are you sure?')) {
      clearVaultStorage()
      importStatus = 'Vault data cleared. Reloading app...'
      reloadTimer.restart()
    }
  }
  
  function reloadApp() {
    window.location.reload()
  }
  
  // Test error handling
  function testErrorHandling() {
    errorService.showSuccess('성공 메시지 테스트', 3000)
    
    setTimeout(() => {
      errorService.showWarning('경고 메시지 테스트', 4000)
    }, 1000)
    
    setTimeout(() => {
      errorService.addError('에러 메시지 테스트', {
        severity: 'error',
        duration: 5000,
        context: { component: 'DevMenu', action: 'testError' }
      })
    }, 2000)
    
    setTimeout(() => {
      errorService.handleApiError(
        new Error('Network error: Failed to fetch'),
        { component: 'DevMenu', action: 'testNetworkError' }
      )
    }, 3000)
  }
  
  async function resetUsageLimit() {
    importStatus = 'Resetting usage limits...'
    
    const [result, error] = await tryExecute(
      async () => {
        // Use UsageTrackingService to reset everything
        const { usageTracker } = await import('$lib/services/UsageTrackingService')
        const success = await usageTracker.resetUsageLimits()
        
        if (success) {
          importStatus = 'Usage limits reset successfully. Reloading app...'
          errorService.showSuccess('사용 한도가 초기화되었습니다. 앱을 다시 시작합니다...')
          
          // Force reload after a short delay to ensure everything is cleared
          reloadTimer.restart()
        } else {
          throw new Error('Failed to reset usage limits')
        }
        
        return success
      },
      {
        severity: 'error',
        context: { action: 'resetUsageLimits' },
        showToUser: true
      }
    )
    
    if (error) {
      importStatus = 'Error resetting usage limits.'
      errorService.handleApiError(error, { component: 'DevMenu', action: 'resetUsageLimits' })
    }
  }
</script>

{#if showDevMenu}
  <div class="dev-menu">
    <div class="dev-menu-header">
      <h3>🛠️ Dev Menu</h3>
      <button class="close-btn" onclick={() => showDevMenu = false}>✕</button>
    </div>
    
    <div class="dev-menu-content">
      <div class="dev-section">
        <h4>API Keys</h4>
        <button class="dev-btn" onclick={importApiKeysFromEnv}>
          Import API Keys from .env
        </button>
        
        {#if importStatus}
          <div class="import-status {importStatus.includes('Success') ? 'success' : importStatus.includes('Error') || importStatus.includes('Failed') ? 'error' : ''}">
            {importStatus}
            
            {#if Object.keys(importedKeys).length > 0}
              <ul class="imported-list">
                {#each Object.entries(importedKeys) as [provider, imported]}
                  {#if imported}
                    <li>✓ {provider}</li>
                  {/if}
                {/each}
              </ul>
            {/if}
          </div>
        {/if}
      </div>
      
      <div class="dev-section">
        <h4>Usage Limits</h4>
        <button class="dev-btn" onclick={resetUsageLimit}>
          Reset Usage Limits
        </button>
        <p class="dev-hint">Reset daily grammar check limits</p>
      </div>
      
      <div class="dev-section">
        <h4>Storage</h4>
        <button class="dev-btn danger" onclick={clearLocalStorage}>
          Clear All Storage
        </button>
        <button class="dev-btn warning" onclick={clearRecentVaults}>
          Clear Vault & Recent Folders
        </button>
        <p class="dev-hint">Remove vault history and folder structure</p>
      </div>
      
      <div class="dev-section">
        <h4>App</h4>
        <button class="dev-btn" onclick={reloadApp}>
          Reload App
        </button>
      </div>
      
      <div class="dev-section">
        <h4>Error Handling Test</h4>
        <button class="dev-btn" onclick={testErrorHandling}>
          Test Error Messages
        </button>
        <p class="dev-hint">Shows different error types</p>
      </div>
      
      <div class="dev-info">
        <p>Press <kbd>Cmd</kbd> + <kbd>Shift</kbd> + <kbd>D</kbd> to toggle</p>
      </div>
    </div>
  </div>
{/if}

<style>
  .dev-menu {
    position: fixed;
    top: 20px;
    right: 20px;
    width: 320px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  .dev-menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .dev-menu-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
  }
  
  .close-btn:hover {
    background: #f3f4f6;
    color: #111827;
  }
  
  .dev-menu-content {
    padding: 16px;
  }
  
  .dev-section {
    margin-bottom: 20px;
  }
  
  .dev-section:last-child {
    margin-bottom: 0;
  }
  
  .dev-section h4 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .dev-btn {
    width: 100%;
    padding: 8px 12px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .dev-btn:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }
  
  .dev-btn.danger {
    background: #fee2e2;
    border-color: #fecaca;
    color: #991b1b;
  }
  
  .dev-btn.danger:hover {
    background: #fecaca;
    border-color: #fca5a5;
  }
  
  .dev-btn.warning {
    background: #fef3c7;
    border-color: #fde68a;
    color: #92400e;
  }
  
  .dev-btn.warning:hover {
    background: #fde68a;
    border-color: #fcd34d;
  }
  
  .import-status {
    margin-top: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    background: #f3f4f6;
    color: #4b5563;
  }
  
  .import-status.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  .import-status.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  .imported-list {
    margin: 8px 0 0 0;
    padding-left: 20px;
    list-style: none;
  }
  
  .imported-list li {
    font-size: 12px;
    margin: 2px 0;
  }
  
  .dev-info {
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
  }
  
  .dev-info p {
    margin: 0;
    font-size: 11px;
    color: #9ca3af;
    text-align: center;
  }
  
  kbd {
    display: inline-block;
    padding: 2px 4px;
    font-size: 10px;
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .dev-menu {
      background: #1f2937;
      border-color: #374151;
    }
    
    .dev-menu-header {
      border-bottom-color: #374151;
    }
    
    .dev-menu-header h3 {
      color: #e5e7eb;
    }
    
    .close-btn {
      color: #9ca3af;
    }
    
    .close-btn:hover {
      background: #374151;
      color: #e5e7eb;
    }
    
    .dev-section h4 {
      color: #9ca3af;
    }
    
    .dev-btn {
      background: #374151;
      border-color: #4b5563;
      color: #e5e7eb;
    }
    
    .dev-btn:hover {
      background: #4b5563;
      border-color: #6b7280;
    }
    
    .dev-btn.danger {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.3);
      color: #f87171;
    }
    
    .dev-btn.danger:hover {
      background: rgba(239, 68, 68, 0.2);
      border-color: rgba(239, 68, 68, 0.4);
    }
    
    .import-status {
      background: #374151;
      color: #d1d5db;
    }
    
    .import-status.success {
      background: rgba(6, 78, 59, 0.3);
      color: #86efac;
      border-color: #064e3b;
    }
    
    .import-status.error {
      background: rgba(127, 29, 29, 0.3);
      color: #fca5a5;
      border-color: #7f1d1d;
    }
    
    .dev-info {
      border-top-color: #374151;
    }
    
    .dev-info p {
      color: #6b7280;
    }
    
    kbd {
      color: #e5e7eb;
      background: #374151;
      border-color: #4b5563;
    }
  }
</style>